import { omit, uniqueId } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM, { flushSync } from 'react-dom';

/** @internal */
export interface ContainerSize {
  /** The width of the container. */
  width: number;
  /** The height of the container. */
  height: number;
}

/**
 * Child props that need to be spread on each child for proper measurment
 * @internal
 */
export type UseMeasureChildrenChildProps = {
  /**
   * Unique child id used to identify the child
   */
  'data-id'?: string;
};

/** @internal */
export type UseMeasureChildrenReturnType<T> = {
  /**
   * CallbackRef for the container element containing the children to be measured
   */
  containerRef: (node: T) => void;
  /**
   * Contains an entry for each child with its corresponding measurements.
   * The unique data-id is used as a key.
   */
  measurements: { [key: string]: ContainerSize | undefined };
  /**
   * Array of child props that need to be spread on each child for proper measurement
   */
  childProps: UseMeasureChildrenChildProps[];
};

/**
 * The hook _useMeasureChildren can be used to measure each direct child in a container.
 * @internal
 */
export const useMeasureChildren = <
  T extends HTMLElement = HTMLDivElement,
>(): UseMeasureChildrenReturnType<T> => {
  const [measurements, setMeasurements] = useState({});
  const [childProps, setChildProps] = useState<UseMeasureChildrenChildProps[]>(
    [],
  );

  // Container used for querying all the children for measuring.
  const containerRef = useRef<T | null>(null);

  // Internally used child props state to avoid running into endless updates
  // with the mutation callback and effect for initializing the resize observer.
  const _childProps = useRef<UseMeasureChildrenChildProps[]>([]);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const mutationObserver = useRef<MutationObserver | null>(null);

  // Callback updating the measurements if the size of a child changes.
  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    const updatedEntries: { [key: string]: ContainerSize } = {};

    for (const entry of entries) {
      const entryId = (entry.target as HTMLElement).dataset.id;

      if (!entryId) {
        // eslint-disable-next-line no-console
        console.warn(
          'No ID found for element so there will be no measurement available. Maybe you need to spread props?',
        );
        continue;
      }

      // Originally wanted to use entry.target.clientWidth, but due to the
      // browser rounding the values there already, collapsing components might
      // run into endlessly collapsing / expanding.
      // Have to use the client rect to get the floating point values.
      const { width, height } = entry.target.getBoundingClientRect();

      updatedEntries[entryId] = {
        width,
        height,
      };
    }

    setMeasurements((prevEntries) => ({
      ...prevEntries,
      ...updatedEntries,
    }));
  }, []);

  // Callback handling added / removed children.
  const handleMutation = useCallback((mutationList: MutationRecord[]) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'childList') {
        // An item has been added to the children.
        if (mutation.addedNodes.length) {
          // Get the index of the previous sibling to insert the needed childProps
          // at the appropriate position.
          const previousSiblingId =
            (mutation.previousSibling as HTMLElement)?.dataset.id || -1;
          const previousSiblingIndex = _childProps.current.indexOf(
            _childProps.current.find((props) => {
              return props['data-id'] === previousSiblingId;
            }) || {},
          );

          // Add the new item to the resize observer.
          resizeObserver.current?.observe(
            mutation.addedNodes[0] as HTMLElement,
          );

          // Create new unique ID for the added child.
          const uniqueChildId = uniqueId();
          // Re-arrange child props array to match the order of the currently rendered
          // children without changing the IDs of already existing children.
          _childProps.current.splice(Math.max(previousSiblingIndex + 1, 0), 0, {
            'data-id': uniqueChildId,
          });

          // Needed to correctly apply the child props to measured children with react 18.
          flushSync(() => {
            setChildProps([..._childProps.current]);
          });
        } else if (mutation.removedNodes.length) {
          // A child has been removed from the container.
          const childId =
            (mutation.removedNodes[0] as HTMLElement).dataset.id || '';
          const childIndex = _childProps.current.indexOf(
            _childProps.current.find((props) => {
              return props['data-id'] === childId;
            }) || {},
          );

          // Remove child from the observed entries.
          resizeObserver.current?.unobserve(
            mutation.removedNodes[0] as HTMLElement,
          );

          // Re-arrange child props array to match the order of the currently rendered
          // children without changing the IDs of already existing children.
          _childProps.current.splice(childIndex, 1);

          // State updates inside native event handlers are not batched and would cause
          // the component using the hook to rerender multiple times.
          ReactDOM.unstable_batchedUpdates(() => {
            setChildProps([..._childProps.current]);
            // Clean up state removing the measurement of the removed node.
            setMeasurements((prevMeasurements) => ({
              ...omit(prevMeasurements, childId),
            }));
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      // Clean up observer when unmounting.
      resizeObserver.current?.disconnect();
      mutationObserver.current?.disconnect();
    };
  }, []);

  const setContainerRef = useCallback(
    (node: T) => {
      if (node && containerRef.current !== node) {
        containerRef.current = node;

        if (resizeObserver.current && mutationObserver.current) {
          resizeObserver.current.disconnect();
          mutationObserver.current.disconnect();
          setMeasurements({});
        }

        const observer = new ResizeObserver(handleResize);
        resizeObserver.current = observer;

        const childIds: UseMeasureChildrenChildProps[] = [];

        // Observe all the children of the container.
        for (const [index, item] of Array.from(
          containerRef.current?.children || [],
        ).entries()) {
          let childId = (item as HTMLElement).dataset.id;
          if (!childId) {
            childId = uniqueId();
          }
          childIds[index] = { 'data-id': childId };

          observer.observe(item);
        }

        _childProps.current = childIds;
        setChildProps([..._childProps.current]);

        const _mutationObserver = new MutationObserver(handleMutation);
        mutationObserver.current = _mutationObserver;
        containerRef.current &&
          _mutationObserver.observe(containerRef.current, {
            childList: true,
          });
      }
    },
    [handleMutation, handleResize],
  );

  return {
    containerRef: setContainerRef,
    measurements,
    childProps,
  };
};
